import styled from 'styled-components';

import type { Color } from '@genially/design-system';
import { BasicSubtitle, SmallParagraph, style } from '@genially/design-system';

import {
  QuestionTitleWrapper,
  TotalParticipantsBigSubtitle,
} from '../../../../SidebarSummary.styled';

interface FullWidthProps {
  $isFullWidthEnabled?: boolean;
}

interface OpenAnswerResponseProps extends FullWidthProps {
  $isSelected?: boolean;
  $isMobile?: boolean;
}

const getAnswerBackground = (color: Color, $isSelected?: boolean) => {
  if ($isSelected) {
    return color.background.primary.active(0.1);
  }

  return color.background.primary.disabledContrast(0.05);
};

export const Wrapper = styled.div<FullWidthProps>(
  ({ theme: { size }, $isFullWidthEnabled }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    width: $isFullWidthEnabled ? '100%' : undefined,
    minHeight: 0,
    paddingTop: $isFullWidthEnabled ? 0 : size.units(2.5),

    [QuestionTitleWrapper]: {
      marginBottom: $isFullWidthEnabled ? size.units(4) : 0,
    },

    [TotalParticipantsBigSubtitle]: {
      marginBottom: size.units(2),
    },
  }),
);

export const AnswerTitleWrapper = styled.div(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}));

export const AnswerTitle = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  gap: size.units(0.5),
  overflow: 'hidden',
  width: '100%',
}));

interface StyledBasicSubtitleProps {
  $hasSpaces: boolean;
}

export const StyledBasicSubtitle = styled(BasicSubtitle)<StyledBasicSubtitleProps>(
  ({ theme: { color }, $hasSpaces }) => ({
    overflow: 'hidden',
    color: color.content.reversed.default(),

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: $hasSpaces ? 'break-word' : 'anywhere',
  }),
);

export const StyledSmallParagraph = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.primary.disabled(),
}));

export const UserInfoWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  gap: size.units(1),
  alignItems: 'flex-start',
  width: '100%',
}));

export const StyledToggleVisibilityIcon = styled.i<{ $show: boolean }>(
  ({ theme: { size, transition }, $show }) => ({
    cursor: 'pointer',
    display: $show ? 'block' : 'none',
    flexShrink: 0,
    fontSize: size.units(1.75),
    transition: transition.standard(),
  }),
);

export const OpenAnswerResponse = styled.div<OpenAnswerResponseProps>(
  ({ theme: { size, color, borderRadius }, $isSelected, $isMobile }) => ({
    borderRadius: borderRadius.medium,
    border: $isSelected ? `1px solid ${color.border.reversed.default()}` : undefined,
    padding: `${size.units(2)} ${size.units(1.5)}`,
    backgroundColor: getAnswerBackground(color, $isSelected),

    [AnswerTitleWrapper]: {
      marginBottom: size.units(1.5),
    },

    [StyledToggleVisibilityIcon]: {
      marginRight: size.units(2),
    },

    ':hover': {
      ...(!$isMobile && {
        [StyledToggleVisibilityIcon]: {
          display: 'block',
        },
      }),
    },
  }),
);

export const AnswerText = styled(BasicSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  fontStyle: 'italic',
  wordBreak: 'break-word',
  textAlign: 'left',
  whiteSpace: 'pre-line',
}));

export const UsernameWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1),
  justifyContent: 'space-between',
}));
